import React, { useState, useMemo, useCallback, useRef } from 'react';
import type { TelemetrySample, BrakingZoneData, BrakingLapEntry } from '@/types/telemetry';
import type { TrackShape, TrackAnnotation } from '@/lib/spline';
import { sampleClosedSpline, computeRibbonBoundaries } from '@/lib/spline';
import { InteractiveTrackMap } from '@/components/common/trackmap/InteractiveTrackMap';
import type { OverlayUtils } from '@/components/common/trackmap/InteractiveTrackMap';
import { haversineDistanceMeters, projectToLocalMeters } from '@/lib/map/mapGeometry';
import type { Point2D, GeoCoord } from '@/lib/map/mapGeometry';
import {
  drawCornerTrackHighlights,
  getCornerSplineIndices,
  getCornerGpsIndices,
} from '@/lib/map/trackGeometry';
import type { CornerHighlightItem, CornerCanvasHitItem } from '@/lib/map/trackGeometry';

interface BrakingMapProps {
  samples: TelemetrySample[];
  zones: BrakingZoneData[];
  selectedZoneIndex: number;
  onSelectZone: (index: number) => void;
  activeLap?: BrakingLapEntry | null;
  hoveredSampleIndex?: number | null;
  trackShape?: TrackShape | null;
  trackAnnotations?: TrackAnnotation[];
}

export function BrakingMap({
  samples,
  zones,
  selectedZoneIndex,
  onSelectZone,
  activeLap,
  hoveredSampleIndex,
  trackShape,
  trackAnnotations,
}: BrakingMapProps) {
  const [hoveredZoneIndex, setHoveredZoneIndex] = useState<number>(-1);

  const referencePoints = useMemo(() => {
    return samples
      .filter(s => s.latitudeRaw && s.longitudeRaw)
      .map(s => ({ lat: s.latitudeRaw / 1e6, lng: s.longitudeRaw / 1e6 }));
  }, [samples]);

  const hasShape = !!(trackShape && trackShape.centerline && trackShape.centerline.length >= 3);
  const trackWidthM = trackShape?.trackWidthM || 8.0;

  const splineSamples = useMemo(() => {
    if (!hasShape) return [];
    return sampleClosedSpline(trackShape!.centerline, trackWidthM, 30);
  }, [hasShape, trackShape, trackWidthM]);

  const boundaries = useMemo(() => {
    if (!hasShape) return { inner: [], outer: [], totalLengthM: 0 };
    return computeRibbonBoundaries(trackShape!.centerline, trackWidthM, 30);
  }, [hasShape, trackShape, trackWidthM]);

  const cornerItems = useMemo<CornerHighlightItem[]>(() => {
    return zones.map((z, i) => {
      const annot = (trackAnnotations || []).find(
        a =>
          a.type === 'corner' &&
          (a.id === z.cornerId || a.id === z.zoneId || a.orderIndex === z.orderIndex || a.orderIndex === i + 1)
      );

      const entryPoint =
        z.entryPoint ||
        (annot?.entry
          ? { lat: annot.entry.lat, lng: annot.entry.lng }
          : annot?.location
          ? { lat: annot.location.lat, lng: annot.location.lng }
          : undefined);

      const exitPoint =
        z.exitPoint ||
        (annot?.exit
          ? { lat: annot.exit.lat, lng: annot.exit.lng }
          : annot?.location
          ? { lat: annot.location.lat, lng: annot.location.lng }
          : undefined);

      const apexPoint =
        z.apexPoint ||
        (annot?.apex
          ? { lat: annot.apex.lat, lng: annot.apex.lng }
          : annot?.location
          ? { lat: annot.location.lat, lng: annot.location.lng }
          : entryPoint);

      return {
        index: i,
        orderIndex: i + 1,
        label: String(i + 1),
        apexPoint,
        entryPoint,
        exitPoint,
      };
    });
  }, [zones, trackAnnotations]);

  const zoneSpans = useMemo(() => {
    return cornerItems.map((c) => {
      if (!c.apexPoint) return { index: c.index, points: [] };
      if (hasShape && splineSamples.length > 0) {
        const indices = getCornerSplineIndices(c.entryPoint, c.apexPoint, c.exitPoint, splineSamples);
        return {
          index: c.index,
          points: indices.map(idx => splineSamples[idx].point),
        };
      }
      if (referencePoints.length > 1) {
        const indices = getCornerGpsIndices(c.entryPoint, c.apexPoint, c.exitPoint, referencePoints);
        return {
          index: c.index,
          points: indices.map(idx => referencePoints[idx]),
        };
      }
      return { index: c.index, points: [c.apexPoint] };
    });
  }, [cornerItems, hasShape, splineSamples, referencePoints]);

  const cornerHitItemsRef = useRef<CornerCanvasHitItem[]>([]);

  const renderOverlay = useCallback(
    (ctx: CanvasRenderingContext2D, { toCanvas, anchor }: OverlayUtils) => {
      // 1. Draw corner track highlights and apex badges
      cornerHitItemsRef.current = drawCornerTrackHighlights(ctx, cornerItems, anchor, toCanvas, {
        selectedIndex: selectedZoneIndex,
        hoveredIndex: hoveredZoneIndex,
        showBadges: true,
        splineSamples,
        boundaries,
        gpsPoints: referencePoints,
      });

      // 2. Draw Braking Start Point for active lap or active zone
      const selectedZone = zones[selectedZoneIndex];
      const bIdx = activeLap?.brakeStartIndex ?? 0;
      const brakeStartPt =
        activeLap?.brakeStartPoint ||
        activeLap?.brakeStartGps ||
        (activeLap?.samples &&
        activeLap.samples.length > bIdx &&
        activeLap.samples[bIdx].latitudeRaw &&
        activeLap.samples[bIdx].longitudeRaw
          ? { lat: activeLap.samples[bIdx].latitudeRaw / 1e6, lng: activeLap.samples[bIdx].longitudeRaw / 1e6 }
          : null) ||
        selectedZone?.brakePoint ||
        selectedZone?.entryPoint;
      if (brakeStartPt) {
        const bCanvas = toCanvas(projectToLocalMeters(brakeStartPt, anchor));

        // Red braking marker dot
        ctx.beginPath();
        ctx.arc(bCanvas.x, bCanvas.y, 6, 0, Math.PI * 2);
        ctx.fillStyle = '#ef4444';
        ctx.fill();
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 1.5;
        ctx.stroke();

        // Marker label
        ctx.fillStyle = '#ffffff';
        ctx.font = 'bold 8px monospace';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('B', bCanvas.x, bCanvas.y);
      }

      // 3. Draw hover scrub reticle if user is scrubbing on G-G diagram or chart
      if (
        hoveredSampleIndex != null &&
        activeLap?.samples &&
        hoveredSampleIndex >= 0 &&
        hoveredSampleIndex < activeLap.samples.length
      ) {
        const sample = activeLap.samples[hoveredSampleIndex];
        if (sample.latitudeRaw && sample.longitudeRaw) {
          const geo = { lat: sample.latitudeRaw / 1e6, lng: sample.longitudeRaw / 1e6 };
          const kartCanvas = toCanvas(projectToLocalMeters(geo, anchor));

          // Outer glowing ring
          ctx.beginPath();
          ctx.arc(kartCanvas.x, kartCanvas.y, 11, 0, Math.PI * 2);
          ctx.strokeStyle = '#38bdf8';
          ctx.lineWidth = 2;
          ctx.stroke();

          // Inner dot
          ctx.beginPath();
          ctx.arc(kartCanvas.x, kartCanvas.y, 5, 0, Math.PI * 2);
          ctx.fillStyle = '#38bdf8';
          ctx.fill();
          ctx.strokeStyle = '#ffffff';
          ctx.lineWidth = 1.5;
          ctx.stroke();
        }
      }
    },
    [
      cornerItems,
      selectedZoneIndex,
      hoveredZoneIndex,
      splineSamples,
      boundaries,
      referencePoints,
      zones,
      activeLap,
      hoveredSampleIndex,
    ]
  );

  const findHitZone = useCallback(
    (canvasPt: Point2D, geoPt: GeoCoord | null) => {
      if (!zones || zones.length === 0) return -1;

      // 1. Direct canvas badge hit test
      for (const item of cornerHitItemsRef.current) {
        const distCanvas = Math.hypot(canvasPt.x - item.apex.x, canvasPt.y - item.apex.y);
        if (distCanvas <= 22) {
          return item.index;
        }
      }

      // 2. Geographic distance check
      if (geoPt) {
        let bestIdx = -1;
        let bestDistM = Infinity;

        for (const span of zoneSpans) {
          for (const pt of span.points) {
            const dist = haversineDistanceMeters(pt, geoPt);
            if (dist <= 25 && dist < bestDistM) {
              bestDistM = dist;
              bestIdx = span.index;
            }
          }
        }

        if (bestIdx >= 0) {
          return bestIdx;
        }
      }

      return -1;
    },
    [zones, zoneSpans]
  );

  const handleClick = useCallback(
    (canvasPt: Point2D, _metricPt: Point2D, geoPt: GeoCoord) => {
      const hitIdx = findHitZone(canvasPt, geoPt);
      if (hitIdx >= 0) {
        onSelectZone(hitIdx);
      }
    },
    [findHitZone, onSelectZone]
  );

  const handleHover = useCallback(
    (canvasPt: Point2D | null, _metricPt: Point2D | null, geoPt: GeoCoord | null) => {
      if (!canvasPt) {
        setHoveredZoneIndex(-1);
        return;
      }
      const hitIdx = findHitZone(canvasPt, geoPt);
      setHoveredZoneIndex(hitIdx);
    },
    [findHitZone]
  );

  return (
    <div className="relative aspect-square w-full mx-auto overflow-hidden rounded-2xl shadow-2xl border border-white/10">
      <InteractiveTrackMap
        trackShape={trackShape}
        trackAnnotations={trackAnnotations}
        points={referencePoints}
        renderOverlay={renderOverlay}
        onClick={handleClick}
        onHover={handleHover}
        cursor={hoveredZoneIndex >= 0 ? 'pointer' : undefined}
        className="w-full h-full"
      />
    </div>
  );
}
